import { db } from './Database';

export interface DefectKey {
    id?: number;
    name: string;
    defectType: 'critical' | 'major1' | 'major2' | 'minor';
    packageType: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface RQL {
    id?: number;
    palletId: number;
    variant: 'preShip' | 'postShip' | 'postStack';
    targetSampleSize: string | number;
    actualSampleSize: string | number;
    dateOfInspection: string;
    packageType: string;
    inspectedBy: string;
    totalDefects: number;
    percentDefects: number;
    passFail: string;
    unclassifiedDefects: string;
    rqlNotes: string;
    criticalTotalDefects?: number | null;
    criticalPercentageDefects?: number | null;
    criticalPassFail?: string | null;
    major1TotalDefects?: number | null;
    major1PercentageDefects?: number | null;
    major1PassFail?: string | null;
    major2TotalDefects?: number | null;
    major2PercentageDefects?: number | null;
    major2PassFail?: string | null;
    minorTotalDefects?: number | null;
    minorPercentageDefects?: number | null;
    minorPassFail?: string | null;
    isOnline?: boolean;
    onlineId?: number;
    createdAt?: string;
    updatedAt?: string;
    defects?: RQLDefect[];
}

export interface RQLDefect {
    id?: number;
    rqlId?: number;
    defectKeyId: number;
    defectType: 'critical' | 'major1' | 'major2' | 'minor';
    defectPercentage: number;
    defectKey?: DefectKey;
    isOnline?: boolean;
    onlineId?: number;
    createdAt?: string;
    updatedAt?: string;
}

export class RqlService {
    // Default defect keys to be added if none exist
    private static readonly DEFAULT_DEFECT_KEYS: DefectKey[] = [
        { name: "Cracks, Holes, Perforations", defectType: 'critical', packageType: 'bottle' },
        { name: "Label (wrong or none)", defectType: 'critical', packageType: 'bottle' },
        { name: "Finish Damage, Finish Molding Defects or Flange", defectType: 'critical', packageType: 'bottle' },
        { name: "Flange/Neck Ring Damage", defectType: 'critical', packageType: 'bottle' },
        { name: "Incorrect or Mixed Bottles", defectType: 'critical', packageType: 'bottle' },
        { name: "Internal/External Contamination", defectType: 'critical', packageType: 'bottle' },
        { name: "Embedded Contamination (metal, glass or other extraneous material)", defectType: 'critical', packageType: 'bottle' },
        { name: "Peeling/Loose Material inside or Internal flash", defectType: 'critical', packageType: 'bottle' },
        { name: "Odor", defectType: 'critical', packageType: 'bottle' },
        { name: "Wrong or Missing Layer, color, or other additive", defectType: 'critical', packageType: 'bottle' },
        { name: "Date Code (Missing or non-legible)", defectType: 'critical', packageType: 'bottle' },
        { name: "Air bubbles or surface depressions > 3mm", defectType: 'critical', packageType: 'bottle' },
        { name: "Crushed or Severely Dented", defectType: 'critical', packageType: 'bottle' },
        { name: "Incorrect or Mixed Cans", defectType: 'critical', packageType: 'bottle' },
        { name: "Label (wrong or none) on Can", defectType: 'critical', packageType: 'bottle' },
        { name: "Internal/External Contamination in Can", defectType: 'critical', packageType: 'bottle' },
        { name: "Air bubbles or surface depressions in Can > 3mm", defectType: 'critical', packageType: 'bottle' },
        { name: "Dimensional – Bottle FINISH", defectType: 'major1', packageType: 'bottle' },
        { name: "Performance (other than barrier)", defectType: 'major1', packageType: 'bottle' },
        { name: "Volume", defectType: 'major1', packageType: 'bottle' },
        { name: "Crushed or Severely Dented", defectType: 'major1', packageType: 'bottle' },
        { name: "Rocker Bottoms", defectType: 'major1', packageType: 'bottle' },
        { name: "Under-blown (Diamond Feet, miss-formed, malformed)", defectType: 'major1', packageType: 'bottle' },
        { name: "Mold Line Miss-Match or External Body Flash (flash not on threads)", defectType: 'major1', packageType: 'bottle' },
        { name: "Off Center Gate (> 6 mm from center)", defectType: 'major1', packageType: 'bottle' },
        { name: "Delamination (filling line impact)", defectType: 'major1', packageType: 'bottle' },
        { name: "Flange/Neck ring damage (20 Tg 12oz Popeye Bottle)", defectType: 'major1', packageType: 'bottle' },
        { name: "Air bubbles or surface depressions > 1mm & 3mm", defectType: 'major1', packageType: 'bottle' },
        { name: "Dimensional – Bottle BODY", defectType: 'major2', packageType: 'bottle' },
        { name: "Material Distribution (other than barrier related)", defectType: 'major2', packageType: 'bottle' },
        { name: "Embedded Contamination (non-metal/glass)", defectType: 'major2', packageType: 'bottle' },
        { name: "Denting / Creasing", defectType: 'major2', packageType: 'bottle' },
        { name: "Label (pre-labeled or self labeled)", defectType: 'major2', packageType: 'bottle' },
        { name: "Delamination (cosmetic)", defectType: 'minor', packageType: 'bottle' },
        { name: "Color (level outside of spec range)", defectType: 'minor', packageType: 'bottle' },
        { name: "Opacity", defectType: 'minor', packageType: 'bottle' },
        { name: "Clarity (Haze, Pearlescence, Crystallinity, etc.)", defectType: 'minor', packageType: 'bottle' },
        { name: "Off Center Gate (< 6 mm and > 3 mm from center)", defectType: 'minor', packageType: 'bottle' },
        { name: "Appearance", defectType: 'minor', packageType: 'bottle' },
        { name: "Air Bubbles or surface depressions >1mm", defectType: 'minor', packageType: 'bottle' },



        { name: "Defective Lithography/Scuffs/Scratches: Any defect that makes the legal or regulatory information or brand identification on the can illegible. Defect leading to unscannable barcode.", defectType: 'critical', packageType: 'can' },
        { name: "Incorrect or mixed cans", defectType: 'critical', packageType: 'can' },
        { name: "Metal sliver: Any sliver or sharp piece of metal that could cut or harm a consumer", defectType: 'critical', packageType: 'can' },
        { name: "Damage to the cans, ends, or tabs that could injure a consumer in any way", defectType: 'critical', packageType: 'can' },
        { name: "Crack, Hole, Perforation, or Failed Seam of any size causing gas or liquid leak, and pinholes and other leaks in CAT4 and thermally processed products", defectType: 'critical', packageType: 'can' },
        { name: "Odor", defectType: 'critical', packageType: 'can' },
        { name: "Broken Tamper Band (in resealable ends): Tamper evident performance is compromised", defectType: 'critical', packageType: 'can' },
        { name: "Missing or Illegible Product Code", defectType: 'critical', packageType: 'can' },
        { name: "External Contamination or foreign material such as dirt, oil, or grease", defectType: 'major1', packageType: 'can' },
        { name: "Heel / Shoulder Dents: ≥ 1” (25.4mm) and metal is sharply creased / jagged", defectType: 'major1', packageType: 'can' },
        { name: "Crease / Rail Marks: any size where metal is sharply creased / jagged", defectType: 'major1', packageType: 'can' },
        { name: "Crush: any size where metal is sharply creased / jagged", defectType: 'major1', packageType: 'can' },
        { name: "Wrinkle / z-wrinkle: any size where metal is sharply creased / jagged", defectType: 'major1', packageType: 'can' },
        { name: "Bottom Rim Damage, sharp/jagged creases or pinches in the metal", defectType: 'major1', packageType: 'can' },
        { name: "Dome Reversal", defectType: 'major1', packageType: 'can' },
        { name: "Pinholes or other leaks (non-pasteurized, non-retort, non-hot fill only)", defectType: 'major1', packageType: 'can' },
        { name: "Fractured Body Walls", defectType: 'major1', packageType: 'can' },
        { name: "Missing or damaged tab", defectType: 'major1', packageType: 'can' },
        { name: "Buckled or Fractured Ends", defectType: 'major1', packageType: 'can' },
        { name: "False Seams and Poor or Partial Seams", defectType: 'major1', packageType: 'can' },
        { name: "Large End dents: >1/2” (12.7mm) or any size dents that affect the opening feature", defectType: 'major2', packageType: 'can' },
        { name: "Neck Pleats: any pleat that extends into and damages the flange area", defectType: 'major2', packageType: 'can' },
        { name: "Heel / Shoulder Dents: ≥ 1” (25.4mm), and metal is creased but not jagged or sharply creased", defectType: 'major2', packageType: 'can' },
        { name: "Crease / Rail Marks: any length with bulging and/or feathering where metal is creased but not jagged", defectType: 'major2', packageType: 'can' },
        { name: "Crush: ≥ 1” (25.4mm) where metal is creased, but not jagged", defectType: 'minor', packageType: 'can' },
        { name: "Wrinkle/z-wrinkle: ≥ 2in2 (12.9cm2) where metal is creased, but not jagged", defectType: 'minor', packageType: 'can' },
        { name: "Bottom Rim Damage: deep dents in metal", defectType: 'minor', packageType: 'can' },
        { name: "Defective Lithography/Scuffs/Scratches: Defects that do not affect the legal, regulatory, or brand identification", defectType: 'minor', packageType: 'can' },
        { name: "Medium size End dents: 1/4” (6.4mm) - 1/2” (12.7mm) that do not affect the opening feature", defectType: 'minor', packageType: 'can' },
        { name: "Neck Pleat: deep pleats >1/2” (12.7mm) that do not extend into the flange.", defectType: 'minor', packageType: 'can' },
        { name: "Heel / Shoulder Dents: 1/2”(12.7mm) - 1“ (25.4mm) with soft edges", defectType: 'minor', packageType: 'can' },
        { name: "Crease / Rail Marks: >1” (25.4mm) with pronounced rounded edges or slight bulging", defectType: 'minor', packageType: 'can' },
        { name: "Crush: 1/2” (12.7mm) - 1” (25.4mm) with soft edges", defectType: 'minor', packageType: 'can' },
        { name: "Wrinkle / z-wrinkle: <2 in2 (12.9cm2) with soft edges", defectType: 'minor', packageType: 'can' },
        { name: "Bottom Rim Damage: distinct dents with rounded edges", defectType: 'minor', packageType: 'can' },
        { name: "Defective Lithography/Scuffs/ Scratches or scuffs: Defects that affect the package appearance in a way that may be objectionable to the consumer, between ¼” (6.35mm) to ½”(12.7mm)", defectType: 'minor', packageType: 'can' },
        { name: "Neck Pleats: >1/4” (6.4mm) that do not extend into the flange.", defectType: 'minor', packageType: 'can' },
        { name: "Tamper Band Defects (in resealable ends): Tamper evident performance stressed but not compromised. Tamper band threads are stretched, score is misplaced, etc.", defectType: 'minor', packageType: 'can' },
    ];


    // Get all defect keys, sync with backend if online
    static async getAllDefectKeys(): Promise<{ defectKeys: DefectKey[] }> {
        try {
            // Try to get defect keys from backend first if online
            try {
                const defectKeys = await db.defectKeys.toArray();
                if(defectKeys.length>0){
                    return { defectKeys };
                }else{
                    return { defectKeys: [] };
                }
            } catch (error) {
                console.log('Failed to fetch from backend, using local data:', error);
            }

            // // If offline or backend fetch failed, use local data
            // let defectKeys = await db.defectKeys.toArray();

            // // If no defect keys exist locally, use defaults
            // if (defectKeys.length === 0) {
            //     const timestamp = new Date().toISOString();
            //     await db.transaction('rw', [db.defectKeys], async () => {
            //         for (const key of this.DEFAULT_DEFECT_KEYS) {
            //             await db.defectKeys.add({
            //                 ...key,
            //                 createdAt: timestamp,
            //                 updatedAt: timestamp
            //             });
            //         }
            //     });
            //     defectKeys = await db.defectKeys.toArray();
            // }

            // return { defectKeys };
        } catch (error) {
            console.error('Error getting defect keys:', error);
            throw error;
        }
    }

    // Get RQL details for a pallet and variant
    static async getRQLDetails(palletId: number, variant: string): Promise<{ rqls: RQL[], defectKeys: DefectKey[] }> {
        try {
            // Get pallet with primary packaging details
            const [pallet, primaryPackaging, rqls, defectKeys] = await Promise.all([
                db.pallets.get(palletId),
                db.primaryPackagingDetails.where('palletId').equals(palletId).first(),
                db.rqls.where('[palletId+variant]').equals([palletId, variant]).toArray(),
                this.getAllDefectKeys().then(res => res.defectKeys)
            ]);

            // Get RQL defects for each RQL
            const rqlsWithDefects = await Promise.all(rqls.map(async rql => {
                const defects = await db.rqlDefects
                    .where('rqlId')
                    .equals(rql.id!)
                    .toArray();

                // Add defect key details to each defect
                const defectsWithKeys = defects.map(defect => ({
                    ...defect,
                    defectKey: defectKeys.find(key => key.id === defect.defectKeyId)
                }));

                return {
                    ...rql,
                    defects: defectsWithKeys,
                    packageType: primaryPackaging?.packageType || ''
                };
            }));

            return {
                rqls: rqlsWithDefects,
                defectKeys
            };
        } catch (error) {
            console.error('Error getting RQL details:', error);
            throw error;
        }
    }

    // Create or update RQL data
    static async createRQL(data: { rqlData: RQL, rqlDefects: RQLDefect[] }): Promise<{ rql: RQL & { defects: RQLDefect[] } }> {
        const { rqlData, rqlDefects } = data;

        try {
            const timestamp = new Date().toISOString();
            let rql: RQL;

            // Convert string numbers to actual numbers and ensure types match the database
            const processedRqlData = {
                ...rqlData,
                targetSampleSize: String(rqlData.targetSampleSize),
                actualSampleSize: String(rqlData.actualSampleSize),
                totalDefects: Number(rqlData.totalDefects || 0),
                percentDefects: Number(rqlData.percentDefects || 0),
                variant: rqlData.variant as 'preShip' | 'postShip' | 'postStack'
            };

            if (rqlData.id) {
                await db.rqls.update(rqlData.id, {
                    ...processedRqlData,
                    updatedAt: timestamp
                });
                rql = (await db.rqls.get(rqlData.id))!;
            } else {
                const id = await db.rqls.add({
                    ...processedRqlData,
                    createdAt: timestamp,
                    updatedAt: timestamp
                });
                rql = (await db.rqls.get(id))!;
            }

            // Delete existing RQL defects
            await db.rqlDefects
                .where('rqlId')
                .equals(rql.id!)
                .delete();

            // Create new RQL defects
            const defectsWithKeys = [];
            for (const defect of rqlDefects) {
                if (defect.defectPercentage !== undefined && defect.defectPercentage !== null) {
                    const processedDefect = {
                        ...defect,
                        defectPercentage: Number(defect.defectPercentage)
                    };

                    const id = await db.rqlDefects.add({
                        ...processedDefect,
                        rqlId: rql.id!,
                        createdAt: timestamp,
                        updatedAt: timestamp
                    });
                    const created = await db.rqlDefects.get(id);
                    if (created) {
                        const defectKey = await db.defectKeys.get(defect.defectKeyId);
                        defectsWithKeys.push({ ...created, defectKey });
                    }
                }
            }

            return {
                rql: {
                    ...rql,
                    defects: defectsWithKeys
                }
            };
        } catch (error) {
            console.error('Error creating/updating RQL:', error);
            throw error;
        }
    }
}
